import React, {Dispatch, SetStateAction, useEffect, useRef} from "react";
import { Box, Dialog, DialogActions, DialogContent, useMediaQuery } from "@material-ui/core";
import { Activity, ChatMessage } from "myfitworld-model";
import { useUserProvider } from "../../providers/UserProvider";
import MessagesView from "./MessagesView";
import useFetchMessagesForChat from "src/hooks/messages/useFetchMessagesForChat";
import LoadingSpinner from "../LoadingSpinner";
import ChatTitle from "./ChatTitle";
import ReplyActivity from "./ReplyActivity";
import SendButton from "./SendButton";
import { useMfwThemeProvider } from "myfitworld-utils";

const ChatDialog = ({ isOpen, handleClose, chatIds, userId, avatarURL, userName, replyActivity, setReplyActivity, unreadCount }: Props) => {
  const {user} = useUserProvider();
  const {mfwMuiWebTheme} = useMfwThemeProvider();
  const isMobile = useMediaQuery(mfwMuiWebTheme.breakpoints.down('sm'));
  const {messages, isLoading, hasMore, fetchMoreMessages, onSendMessage, chatId, markAsReadMessages} = useFetchMessagesForChat({chatIds});
  const containerRef = useRef<HTMLDivElement | null>(null); // Ref to the messages container

  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
      });
    }
  };

  const handleLoadMore = async () => {  
    // Fetch more messages
    await fetchMoreMessages();
  
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: 0,
      });
    }
  };
  const sendMessage = async (newMessage: string) => {
    if (!user || !user.id) return;
    let newMessageObj : ChatMessage= {
      threadId: userId,
      senderId: user.id,
      text: newMessage,
      read: false,
      chatId: chatId ?? "",
      createdAt: new Date()
    };

    if(replyActivity) {
      newMessageObj.replyOnWorkoutFeedbackData = {
        threadId: replyActivity.threadId,
        text: replyActivity.text ?? "",
        workoutId: replyActivity.workoutId,
        reaction: replyActivity.reaction,
        workout: replyActivity.workout
      };
    }

    onSendMessage(newMessageObj);
    setReplyActivity && setReplyActivity(undefined);
  };

  useEffect(() => {
    if (isOpen) {
      if (messages.length > 0) {
        scrollToBottom();
      }
    }
  }, [isOpen, messages]); 
  
  return (
    <Dialog
      aria-labelledby="chat-dialog"
      open={isOpen}
      maxWidth={isMobile ? 'sm' : 'md'}
      fullWidth
      onClick={(event) => event.stopPropagation()}
    >
      <ChatTitle 
        avatarURL={avatarURL}
        userName={userName} 
        handleClose={(event) => {handleClose(event); if(unreadCount > 0) markAsReadMessages()}}
      />
      <DialogContent
        ref={containerRef}
        style={{ overflowY: 'scroll', height: 500 }}
      >
        {isLoading && <LoadingSpinner/>}
        <MessagesView 
          userId={userId} 
          messages={messages} 
          hasMore={hasMore}
          fetchMoreMessages={handleLoadMore}
        /> 
      </DialogContent>
      <DialogActions>
        <Box 
          flexDirection='column' 
          width='100%'
        >
          {replyActivity && 
            <ReplyActivity 
              replyActivity={replyActivity} 
              setReplyActivity={setReplyActivity}
            />
          }
          <SendButton 
            markAsReadMessages={markAsReadMessages} 
            sendMessage={sendMessage}
          />
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ChatDialog;

interface Props {
  isOpen: boolean;
  handleClose: (event: any) => void;
  chatIds: string[];
  userId: string;
  unreadCount: number;
  avatarURL?: string;
  userName?: string;
  replyActivity?: Activity;
  setReplyActivity?: Dispatch<SetStateAction<Activity|undefined>>;
}
